import posthog from 'posthog-js';
import { versionName } from 'src/appConfig';
import { PostHogEvent } from './posthogEvents';
export { PostHogEvent } from './posthogEvents';

/**
 * Flag indicating whether PostHog analytics is enabled.
 * Evaluates to `true` if `REACT_APP_POSTHOG_KEY` is defined and non-empty.
 */
export const POSTHOG_ENABLED = Boolean(process.env.REACT_APP_POSTHOG_KEY);

/**
 * Central singleton PostHog analytics service.
 *
 * Handles self-contained PostHog SDK initialization and provides a safe, centralized
 * API for all PostHog interactions across the application — user identification, event
 * tracking, error/exception reporting, SPA pageview capture, person properties, and session cleanup on logout.
 *
 * All public methods are no-ops when `POSTHOG_ENABLED` is `false`
 * (i.e. when `REACT_APP_POSTHOG_KEY` is not set), so callers never
 * need to guard themselves.
 *
 * ### Key Responsibilities:
 * - **Self-Contained Initialization**: Initializes PostHog SDK with US Cloud host and app version.
 * - **User Identification**: Links anonymous sessions to authenticated users via `identify()`.
 * - **Session Reset**: Clears the PostHog distinct ID on logout via `reset()` to prevent cross-user contamination.
 * - **Custom Event Tracking**: Type-safe `track()` method for all conversion/product events.
 * - **Error & Exception Reporting**: `reportError()` / `captureException()` for capturing runtime errors.
 * - **SPA Pageview Tracking**: Manual `$pageview` capture for React Router route changes.
 * - **Person Properties**: Enrich user profiles with metadata (plan, role, etc.).
 *
 * ### Usage:
 * ```ts
 * import { posthogService } from 'src/common/services/posthogService';
 *
 * // Identify user on login
 * posthogService.identify(user._id, { email: user.email, plan: 'pro' });
 *
 * // Track a custom event
 * posthogService.track(PostHogEvent.BILLING_PAYMENT_SUCCESSFUL, { email, amount: 49 });
 *
 * // Capture SPA pageview on route change
 * posthogService.capturePageview();
 *
 * // Report error
 * posthogService.reportError(error, { context: 'checkout' });
 *
 * // Reset on logout
 * posthogService.reset();
 * ```
 */
class PostHogService {
  constructor() {
    this.init();
  }

  /**
   * Initializes the PostHog SDK if `POSTHOG_ENABLED` is true.
   * Configures US Cloud API host, enables exception capture, and registers app version.
   *
   * @private
   */
  private init(): void {
    if (!POSTHOG_ENABLED) return;

    posthog.init(process.env.REACT_APP_POSTHOG_KEY as string, {
      api_host: process.env.REACT_APP_POSTHOG_HOST as string,
      ui_host: process.env.REACT_APP_POSTHOG_HOST as string,
      capture_pageview: false,
      capture_exceptions: true,
      capture_dead_clicks: false,
      autocapture: false,
      rageclick: false,
    });

    posthog.register({ app_version: versionName });
  }

  /**
   * Identifies the current user in PostHog, linking their anonymous session
   * to a persistent distinct ID.
   *
   * Should be called once when `userDetails` becomes available after login/auth.
   * Subsequent calls with the same `userId` are safely deduplicated by the PostHog SDK.
   *
   * @param {string} userId - The unique user identifier (typically `userDetails._id`).
   * @param {Record<string, unknown>} [traits] - Optional person properties to set on the user profile
   *   (e.g. `{ email, name, plan, role }`).
   *
   * @example
   * ```ts
   * posthogService.identify(userDetails._id, {
   *   email: userDetails.email,
   *   name: userDetails.name,
   *   plan: activePlanName,
   *   role: userDetails.role_type
   * });
   * ```
   */
  public identify(userId: string, traits?: Record<string, unknown>): void {
    if (!POSTHOG_ENABLED) return;
    posthog.identify(userId, traits);
  }

  /**
   * Resets the PostHog distinct ID and clears all stored super properties.
   *
   * Must be called on user logout to prevent events from a new anonymous session
   * being incorrectly attributed to the previously authenticated user.
   *
   * @example
   * ```ts
   * const logout = async () => {
   *   await logoutFromApp();
   *   posthogService.reset();
   * };
   * ```
   */
  public reset(): void {
    if (!POSTHOG_ENABLED) return;
    posthog.reset();
  }

  /**
   * Captures a custom analytics event in PostHog.
   *
   * Events are only sent when PostHog is enabled (i.e. API key is configured).
   * All callers can safely invoke this without checking `POSTHOG_ENABLED`.
   *
   * @param {PostHogEvent | string} eventName - The event name (e.g. `PostHogEvent.AUTH_USER_SIGNED_UP`).
   *   Prefer values from `PostHogEvent` enum for consistency, but arbitrary strings are accepted.
   * @param {Record<string, unknown>} [properties] - Optional key-value properties attached to the event.
   *
   * @example
   * ```ts
   * posthogService.track(PostHogEvent.BILLING_TRIAL_PLAN_SELECTED, {
   *   email: user.email,
   *   plan_name: 'Pro'
   * });
   * ```
   */
  public track(
    eventName: PostHogEvent | string,
    properties?: Record<string, unknown>
  ): void {
    if (!POSTHOG_ENABLED) return;
    posthog.capture(eventName, properties);
  }

  /**
   * Sets persistent person properties on the currently identified user's PostHog profile.
   *
   * These properties are merged with any existing properties and persist across sessions.
   *
   * @param {Record<string, unknown>} properties - Key-value pairs to set on the person profile.
   *
   * @example
   * ```ts
   * posthogService.setPersonProperties({
   *   subscription_id: sub.id,
   *   is_plugin_installed: true
   * });
   * ```
   */
  public setPersonProperties(properties: Record<string, unknown>): void {
    if (!POSTHOG_ENABLED) return;
    posthog.setPersonProperties(properties);
  }

  /**
   * Captures a `$pageview` event for SPA (Single Page Application) route changes.
   *
   * `capture_pageview: false` is set in `posthog.init()` so the SDK does NOT auto-fire
   * on load — all pageviews are manually owned by `PostHogPageViewTracker`.
   *
   * Should be paired with `capturePageleave()` on route exit so PostHog can accurately
   * calculate time-on-page and session duration.
   *
   * @example
   * ```ts
   * // Inside PostHogPageViewTracker component
   * useEffect(() => {
   *   posthogService.capturePageview();
   *   return () => posthogService.capturePageleave();
   * }, [location.pathname]);
   * ```
   */
  public capturePageview(): void {
    if (!POSTHOG_ENABLED) return;
    posthog.capture('$pageview');
  }

  /**
   * Captures a `$pageleave` event when the user navigates away from the current route.
   *
   * PostHog uses `$pageleave` alongside `$pageview` to calculate time-on-page
   * and session duration metrics. Without this, duration for the last page in a
   * session is unknown and time-on-page charts will be inaccurate.
   *
   * Should be called as the cleanup return of the same `useEffect` that calls
   * `capturePageview()`, so it fires just before the next route renders.
   *
   * @example
   * ```ts
   * useEffect(() => {
   *   posthogService.capturePageview();
   *   return () => posthogService.capturePageleave();
   * }, [location.pathname]);
   * ```
   */
  public capturePageleave(): void {
    if (!POSTHOG_ENABLED) return;
    posthog.capture('$pageleave');
  }

  /**
   * Captures an error/exception in PostHog with optional contextual metadata.
   *
   * @param {Error} error - The caught JavaScript Error object.
   * @param {Record<string, unknown>} [extra] - Optional metadata / context to associate with the error.
   *
   * @example
   * ```ts
   * posthogService.reportError(err, { boundary: 'ErrorBoundaryV2' });
   * ```
   */
  public reportError(error: Error, extra?: Record<string, unknown>): void {
    if (!POSTHOG_ENABLED) return;
    posthog.captureException(error, extra);
  }
}

/**
 * Shared singleton instance of the PostHogService.
 *
 * @type {PostHogService}
 */
export const posthogService: PostHogService = new PostHogService();

/**
 * Global helper function to report runtime errors / exceptions to PostHog.
 *
 * @param {Error} error - The caught error object.
 * @param {Record<string, unknown>} [extra] - Optional extra metadata or component stack.
 */
export function reportError(error: Error, extra?: Record<string, unknown>): void {
  posthogService.reportError(error, extra);
}
